// Remove this file after the Account_Provisioning_History release.
import React, {useMemo, useState} from 'react';

import {UserLink} from 'src/components/links/user-link';
import {NoResultPage} from 'src/modules/entitlements';
import {DailyUserHistoryEventActionType, DailyUserHistoryEventStatus} from 'src/openapi/admin/models/DailyUserHistoryEvent';

import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {SortHeaderFilter} from '@pb/kit-component-filters';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {PaginationStripe} from '@pb/kit-component-pagination';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';

import type {DailyUserHistoryEvent} from 'src/openapi/admin/models/DailyUserHistoryEvent';

const ALL_ACTIONS = [
    DailyUserHistoryEventActionType.Created,
    DailyUserHistoryEventActionType.Updated,
    DailyUserHistoryEventActionType.Imported,
    DailyUserHistoryEventActionType.Exported,
];
const STATUS_LABELS: Record<DailyUserHistoryEventStatus, string> = {
    [DailyUserHistoryEventStatus.ENABLED]: 'Enabled',
    [DailyUserHistoryEventStatus.DISABLED]: 'Disabled',
    [DailyUserHistoryEventStatus.SUSPENDED]: 'Suspended',
    [DailyUserHistoryEventStatus.EMPTY]: 'Empty',
};
const ALL_USER_TYPES = ['Licensed', 'Mobile Only', 'CRM Only', 'Credit Alerts Only', 'No Access'];

type SortColumn = 'actionType' | 'userType' | 'timestamp';
type SortState = {column: SortColumn | null, direction: 'ASC' | 'DESC'};
type AppliedFilters = {actionType: string[], userType: string[]};

type UnifiedEventsTableProps = {
    eventsData: Array<DailyUserHistoryEvent> | null,
};

const formatTimestamp = (ts: string): string => {
    const date = new Date(ts);
    return date.toLocaleString('en-US', {
        month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false,
    });
};

export const UnifiedEventsTable = ({eventsData}: UnifiedEventsTableProps) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sort, setSort] = useState<SortState>({column: null, direction: 'ASC'});
    const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({actionType: [], userType: []});
    const [draftActionFilter, setDraftActionFilter] = useState<string[]>([]);
    const [draftUserTypeFilter, setDraftUserTypeFilter] = useState<string[]>([]);

    const processedEvents = useMemo(() => {
        let filtered = eventsData ?? [];
        if (appliedFilters.actionType.length > 0) {
            filtered = filtered.filter(e => e.actionType && appliedFilters.actionType.includes(e.actionType as string));
        }
        if (appliedFilters.userType.length > 0) {
            filtered = filtered.filter(e => appliedFilters.userType.includes(e.userType));
        }
        if (sort.column) {
            const col = sort.column;
            const dir = sort.direction === 'ASC' ? 1 : -1;
            if (col === 'timestamp') {
                filtered = [...filtered]
                    .sort((a, b) => (new Date(a.timestamp)
                        .getTime() - new Date(b.timestamp).getTime()) * dir);
            } else {
                filtered = [...filtered]
                    .sort((a, b) => (a[col] ?? '')
                        .localeCompare(b[col] ?? '') * dir);
            }
        }
        return filtered;
    }, [eventsData, appliedFilters, sort]);

    const pagedEvents = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return processedEvents.slice(start, start + pageSize);
    }, [processedEvents, currentPage, pageSize]);

    if (eventsData?.length === 0) {
        return (
            <Group gap="xxl" justify="center">
                No changes on this date
            </Group>
        );
    }

    return (
        <Group gap="m" direction="v">
            <Table>
                <Head>
                    <Cell width={220}>Email</Cell>
                    <Cell width={95}>
                        <SortHeaderFilter
                            caption="Action"
                            sortable={true}
                            direction={sort.column === 'actionType' ? sort.direction : null}
                            defaultDirection="ASC"
                            filterActive={appliedFilters.actionType.length > 0}
                            onSort={dir => {
                                setSort({column: 'actionType', direction: dir}); setCurrentPage(1);
                            }}
                            onApply={() => {
                                setAppliedFilters(prev => ({...prev, actionType: draftActionFilter}));
                                setCurrentPage(1);
                            }}
                            onReset={() => {
                                setDraftActionFilter([]);
                                setAppliedFilters(prev => ({...prev, actionType: []}));
                                setCurrentPage(1);
                            }}
                            onCancel={() => {
                                setDraftActionFilter(appliedFilters.actionType);
                            }}>
                            <div>
                                <Label>Action</Label>
                                <ComboBox
                                    value={draftActionFilter}
                                    onChange={setDraftActionFilter}
                                    multiselectable={true}
                                    placeholder="Select actions">
                                    {ALL_ACTIONS.map(a => <ComboboxItem key={a} caption={a} value={a}/>)}
                                </ComboBox>
                            </div>
                        </SortHeaderFilter>
                    </Cell>
                    <Cell width={115}>
                        <SortHeaderFilter
                            caption="User Type"
                            sortable={true}
                            direction={sort.column === 'userType' ? sort.direction : null}
                            defaultDirection="ASC"
                            filterActive={appliedFilters.userType.length > 0}
                            onSort={dir => {
                                setSort({column: 'userType', direction: dir});
                                setCurrentPage(1);
                            }}
                            onApply={() => {
                                setAppliedFilters(prev => ({...prev, userType: draftUserTypeFilter}));
                                setCurrentPage(1);
                            }}
                            onReset={() => {
                                setDraftUserTypeFilter([]);
                                setAppliedFilters(prev => ({...prev, userType: []}));
                                setCurrentPage(1);
                            }}
                            onCancel={() => {
                                setDraftUserTypeFilter(appliedFilters.userType);
                            }}>
                            <div>
                                <Label>User Type</Label>
                                <ComboBox
                                    value={draftUserTypeFilter}
                                    onChange={setDraftUserTypeFilter}
                                    multiselectable={true}
                                    placeholder="Select user types">
                                    {ALL_USER_TYPES.map(ut => <ComboboxItem key={ut} caption={ut} value={ut}/>)}
                                </ComboBox>
                            </div>
                        </SortHeaderFilter>
                    </Cell>
                    <Cell width={95}>Status</Cell>
                    <Cell>Reason</Cell>
                    <Cell width={220}>Initiator</Cell>
                    <Cell width={135}>
                        <SortHeaderFilter
                            caption="Timestamp"
                            showFilter={false}
                            sortable={true}
                            direction={sort.column === 'timestamp' ? sort.direction : null}
                            defaultDirection="ASC"
                            onSort={dir => {
                                setSort({column: 'timestamp', direction: dir}); setCurrentPage(1);
                            }}
                            onApply={() => {}}
                            onReset={() => {}}
                            onCancel={() => {}}>
                            <></>
                        </SortHeaderFilter>
                    </Cell>
                </Head>
                <Body>
                    {pagedEvents.length > 0 ?
                            pagedEvents.map(event => (
                                <Row key={event.logId}>
                                    <Cell>
                                        {event.userId != null ? (
                                            <UserLink
                                                userId={event.userId}
                                                caption={event.email}
                                                blank={true}
                                                place="User Count History Chart"
                                            />
                                        ) : event.email}
                                    </Cell>
                                    <Cell>{event.actionType ?? ''}</Cell>
                                    <Cell>{event.userType}</Cell>
                                    <Cell>{STATUS_LABELS[event.status]}</Cell>
                                    <Cell>{event.reason ?? ''}</Cell>
                                    <Cell>
                                        {event.initiatorUserEmail && event.initiatorUserId != null ? (
                                            <UserLink
                                                userId={event.initiatorUserId}
                                                caption={event.initiatorUserEmail}
                                                blank={true}
                                                place="User Count History Chart - Initiator"
                                            />
                                        ) : (event.initiatorSystem ?? '')}
                                    </Cell>
                                    <Cell>{formatTimestamp(event.timestamp)}</Cell>
                                </Row>
                            )) : (
                                <Row>
                                    <Cell colSpan={7} className="text-align_center">
                                        <div>
                                            <NoResultPage
                                                title="No data to show"
                                                margin={20}
                                                description="No data found for the selected criteria."
                                            />
                                        </div>
                                    </Cell>
                                </Row>
                            )}
                </Body>
            </Table>
            <PaginationStripe
                pageSize={pageSize}
                totalCount={processedEvents.length}
                currentPage={currentPage}
                onPaginationChange={({currentPage: page}) => setCurrentPage(page)}
                onChangePaginationSize={(_page, size) => {
                    setPageSize(size); setCurrentPage(1);
                }}
                sizeSwitcherPosition="LEFT"
                sizeSwitcherType="ONLY_PAGE_SIZE"
                sizes={[10, 25, 50]}
            />
        </Group>
    );
};
